import type { CriterionTranscriptionAssignment } from '../../types';

export type AssignmentGroupBy = 'criterion' | 'labeller';
export type StatusTab = 'all' | 'open' | 'started';

export interface ProgressSummary {
  assignmentCount: number;
  openCount: number;
  startedCount: number;
  uniqueTranscriptionCount: number;
}

export interface CounterpartProgress {
  id: string;
  label: string;
  total: number;
  started: number;
}

export interface GroupRow {
  id: string;
  label: string;
  total: number;
  open: number;
  started: number;
  transcripts: number;
  counterparts: CounterpartProgress[];
  items: CriterionTranscriptionAssignment[];
}

export const isOpenAssignment = (assignment: CriterionTranscriptionAssignment): boolean =>
  assignment.status === 'open';

export const openAssignmentIds = (assignments: CriterionTranscriptionAssignment[]): string[] =>
  assignments.filter(isOpenAssignment).map(assignment => assignment.id);

export const summarizeAssignments = (
  assignments: CriterionTranscriptionAssignment[]
): ProgressSummary => ({
  assignmentCount: assignments.length,
  openCount: assignments.filter(assignment => assignment.status === 'open').length,
  startedCount: assignments.filter(assignment => assignment.status === 'started').length,
  uniqueTranscriptionCount: new Set(assignments.map(assignment => assignment.transcriptionId)).size,
});

export const byStatus = (
  items: CriterionTranscriptionAssignment[],
  status: StatusTab
): CriterionTranscriptionAssignment[] =>
  status === 'all' ? items : items.filter(assignment => assignment.status === status);

export const pct = (part: number, whole: number): number =>
  whole === 0 ? 0 : Math.round((part / whole) * 100);

export const formatCount = (n: number): string => n.toLocaleString();

export const formatShortDate = (iso: string): string =>
  new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: '2-digit' });

export const buildGroups = (
  assignments: CriterionTranscriptionAssignment[],
  groupBy: AssignmentGroupBy,
  getUserName: (id: string) => string,
  getCriteriaName: (id: string) => string
): GroupRow[] => {
  const key: 'criteriaId' | 'userId' = groupBy === 'criterion' ? 'criteriaId' : 'userId';
  const counterKey: 'criteriaId' | 'userId' = groupBy === 'criterion' ? 'userId' : 'criteriaId';
  const label = groupBy === 'criterion' ? getCriteriaName : getUserName;
  const counterLabel = groupBy === 'criterion' ? getUserName : getCriteriaName;

  const grouped = new Map<string, CriterionTranscriptionAssignment[]>();
  assignments.forEach(assignment => {
    const list = grouped.get(assignment[key]);
    if (list) list.push(assignment);
    else grouped.set(assignment[key], [assignment]);
  });

  return Array.from(grouped.entries())
    .map(([id, items]) => {
      const counter = new Map<string, { total: number; started: number }>();
      items.forEach(assignment => {
        const current = counter.get(assignment[counterKey]) ?? { total: 0, started: 0 };
        current.total += 1;
        if (assignment.status === 'started') current.started += 1;
        counter.set(assignment[counterKey], current);
      });
      const summary = summarizeAssignments(items);
      return {
        id,
        label: label(id),
        total: items.length,
        open: summary.openCount,
        started: summary.startedCount,
        transcripts: summary.uniqueTranscriptionCount,
        counterparts: Array.from(counter.entries())
          .map(([counterpartId, counts]) => ({
            id: counterpartId,
            label: counterLabel(counterpartId),
            ...counts,
          }))
          .sort((left, right) => left.label.localeCompare(right.label)),
        items,
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));
};

export const partitionLabellerPills = (counterparts: CounterpartProgress[]) => ({
  complete: counterparts.filter(item => item.total > 0 && item.started === item.total),
  inProgress: counterparts.filter(item => item.started < item.total),
});
